// src/components/channels/ConfigureWhatsAppBusiness.js
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Eye, EyeOff, CheckCircle, AlertTriangle, 
  ExternalLink, Copy, Info, Lock
} from 'lucide-react';
import { channelService } from '../../services/PlatformService';

const ConfigureWhatsAppBusiness = ({ channel, onUpdate }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [copied, setCopied] = useState(false);
  
  const [formData, setFormData] = useState({
    businessId: '',
    accountId: '',
    chatbotNumber: '',
    phoneNumberId: '',
    accessToken: ''
  });

  useEffect(() => {
    // Se já tem configuração, preencher form
    if (channel.config) {
      setFormData(prev => ({ ...prev, ...channel.config }));
      if (channel.status === 'active') {
        setCurrentStep(3); // Já configurado
      } else if (channel.webhook_verified) {
        setCurrentStep(2); // Webhook configurado mas não ativo
      }
    }
  }, [channel]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleSaveCredentials = async () => {
    setSaving(true);
    try {
      await channelService.updateChannel(channel.id, {
        config: formData
      });
      setCurrentStep(2);
      //onUpdate(); // Refresh channel data
    } catch (error) {
      console.error('Error saving credentials:', error);
      alert('Erro ao salvar credenciais');
    } finally {
      setSaving(false);
    }
  };

  const handleVerifyWebhook = async () => {
    const response = await channelService.getChannel(channel.id);
    const channelData = response.data;
    if (channelData.webhook_verified) {
      setCurrentStep(3);
      onUpdate();
    } else {
      alert('Configure o webhook no Meta Business. A verificação ainda não foi validada.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-blue-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate('/channels')}
            className="p-2 hover:bg-gray-100 rounded-xl transition-all"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">
            Configurar {channel?.name}
          </h1>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {currentStep === 1 && (
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-xl font-bold text-gray-900 mb-6">
              Etapa 1: Configure suas Credenciais
            </h2>

            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">Onde encontrar essas informações:</p>
                  <ol className="list-decimal list-inside space-y-1">
                    <li>Acesse o <a href="https://business.facebook.com" target="_blank" rel="noopener noreferrer" className="underline">Meta Business Suite</a></li>
                    <li>Vá em WhatsApp → Configurações da API</li>
                    <li>Copie as informações solicitadas abaixo</li>
                  </ol>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ID da Conta Empresarial
                  </label>
                  <input
                    type="text"
                    value={formData.businessId}
                    onChange={(e) => setFormData(prev => ({ ...prev, businessId: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-green-500"
                    placeholder="123456789012345"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ID da Conta WhatsApp
                  </label>
                  <input
                    type="text"
                    value={formData.accountId}
                    onChange={(e) => setFormData(prev => ({ ...prev, accountId: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-green-500"
                    placeholder="112233445566778"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Número do WhatsApp
                  </label>
                  <input
                    type="text"
                    value={formData.chatbotNumber}
                    onChange={(e) => setFormData(prev => ({ ...prev, chatbotNumber: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-green-500"
                    placeholder="+55 11 99999-9999"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ID do Número
                  </label>
                  <input
                    type="text"
                    value={formData.phoneNumberId}
                    onChange={(e) => setFormData(prev => ({ ...prev, phoneNumberId: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-green-500"
                    placeholder="998877665544332"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Token de Acesso Permanente
                </label>
                <div className="relative">
                  <input
                    type={showToken ? "text" : "password"}
                    value={formData.accessToken}
                    onChange={(e) => setFormData(prev => ({ ...prev, accessToken: e.target.value }))}
                    className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-green-500 font-mono"
                    placeholder="EAABxxxxxx..."
                  />
                  <button
                    type="button"
                    onClick={() => setShowToken(!showToken)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showToken ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={handleSaveCredentials}
                disabled={saving || !formData.businessId || !formData.accountId || !formData.phoneNumberId || !formData.accessToken}
                className="px-6 py-3 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all"
              >
                {saving ? 'Salvando...' : 'Salvar e Continuar'}
              </button>
            </div>
          </div>
        )}

        {currentStep === 2 && (
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-xl font-bold text-gray-900 mb-6">
              Etapa 2: Configure o Webhook
            </h2>

            <div className="space-y-6">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-800">
                    <p className="font-medium mb-2">Atenção: Siga os passos exatamente como descrito</p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">1. URL do Webhook:</p>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={channel?.webhook_url || ''}
                      readOnly
                      className="flex-1 px-4 py-2 bg-gray-50 border border-gray-300 rounded-xl font-mono text-sm"
                    />
                    <button
                      onClick={() => copyToClipboard(channel?.webhook_url || '')}
                      className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-all"
                    >
                      {copied ? <CheckCircle className="w-5 h-5 text-green-600" /> : <Copy className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">2. Token de Verificação:</p>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value="INFINITAGENTS_VERIFY_TOKEN"
                      readOnly
                      className="flex-1 px-4 py-2 bg-gray-50 border border-gray-300 rounded-xl font-mono text-sm font-bold"
                    />
                    <button
                      onClick={() => copyToClipboard('INFINITAGENTS_VERIFY_TOKEN')}
                      className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-all"
                    >
                      <Copy className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                <div className="bg-blue-50 rounded-xl p-4">
                  <p className="text-sm font-medium text-blue-900 mb-2">3. No Meta for Business:</p>
                  <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                    <li>Acesse WhatsApp → Configuração → Webhooks</li>
                    <li>Cole a URL do Webhook no campo "URL de retorno de chamada"</li>
                    <li>Cole o token no campo "Token de verificação"</li>
                    <li>Clique em "Verificar e salvar"</li>
                    <li>Ative os webhooks de mensagens</li>
                  </ul>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <a 
                  href="https://developers.facebook.com/docs/whatsapp/cloud-api/get-started#configure-webhooks" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-green-600 hover:text-green-700 font-medium"
                >
                  Ver tutorial completo <ExternalLink className="w-4 h-4" />
                </a>

                <button
                  onClick={handleVerifyWebhook}
                  className="px-6 py-3 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 transition-all"
                >
                  <Lock className="w-5 h-5 inline mr-2" />
                  Configurei o Webhook
                </button>
              </div>
            </div>
          </div>
        )}

        {currentStep === 3 && (
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Canal Configurado com Sucesso! 🎉
            </h2>
            
            <p className="text-gray-600 mb-8">
              Seu funcionário está pronto para atender no WhatsApp Business
            </p>
            
            <div className="flex items-center justify-center gap-4">
              <button
                onClick={() => navigate('/channels')}
                className="px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-all"
              >
                Voltar aos Canais
              </button>
              
              <button
                onClick={() => navigate(`/channels/${channel.id}`)}
                className="px-6 py-3 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 transition-all"
              >
                Ver Detalhes
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConfigureWhatsAppBusiness;